import {
  Component, EventEmitter, Input, Output, ViewChild, AfterViewInit, ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { UploadFileComponent } from '../upload-file/upload-file.component';
import { VerificationComponent } from '../verification/verification.component';
import { CoursesService } from 'src/app/services/courses.service';

@Component({
  selector: 'app-upload-course-documents',
  templateUrl: './upload-course-documents.component.html',
  styleUrls: ['./upload-course-documents.component.scss'],
})
export class UploadCourseDocumentsComponent implements AfterViewInit {
  @Input() courseItemId!: number;
  @Output() closeModal = new EventEmitter<void>();
  @Output() documentsUploaded = new EventEmitter<void>();
  @ViewChild('uploadFileComponent') uploadFileComponent!: UploadFileComponent;
  @ViewChild('existingDocumentsComponent') existingDocumentsComponent!: UploadFileComponent;

  private duplicateDocuments: { assetId: number; fileName: string }[] = [];

  constructor(
    private dialog: MatDialog,
    private coursesService: CoursesService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    // Ensure courseItemId is set on both components after view init
    if (this.courseItemId) {
      // Set courseItemId on upload component
      if (this.uploadFileComponent) {
        (this.uploadFileComponent as any).courseItemId = this.courseItemId;
      }
      
      // Set courseItemId on existing documents component and ensure it loads documents
      if (this.existingDocumentsComponent) {
        (this.existingDocumentsComponent as any).courseItemId = this.courseItemId;
        // The existing documents component will automatically fetch documents in ngOnInit
        // but we'll also ensure they're loaded here
        setTimeout(() => {
          this.existingDocumentsComponent.fetchExistingDocuments().subscribe({
            next: (documents) => {
              // Documents are now loaded and will be displayed
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('Failed to load existing documents:', err);
            },
          });
        }, 100);
      }
      
      this.cdr.detectChanges();
    }
  }

  closeModalHandler = () => {
    this.closeModal.emit();
  }

  uploadDocuments = () => {
    if (!this.uploadFileComponent) {
      console.error('UploadFileComponent not available');
      return;
    }

    if (!this.uploadFileComponent.hasFilesToUpload()) {
      console.warn('No files to upload');
      return;
    }

    // Ensure courseItemId is set on the upload component before checking
    if (!this.courseItemId) {
      console.error('Cannot upload: courseItemId is required');
      return;
    }

    // Ensure courseItemId is set on the upload component (both via Input and directly)
    (this.uploadFileComponent as any).courseItemId = this.courseItemId;
    this.cdr.detectChanges();

    // Always check for duplicate documents before uploading
    this.checkForDuplicateDocuments();
  }

  checkForDuplicateDocuments = () => {
    // Get selected files
    const selectedFiles = this.uploadFileComponent.getSelectedFiles();

    if (!selectedFiles || selectedFiles.length === 0) {
      console.warn('No selected files to check for duplicates');
      return;
    }

    // Reset duplicate documents array
    this.duplicateDocuments = [];

    // Ensure we have a courseItemId
    if (!this.courseItemId) {
      console.error('Cannot check for duplicates: courseItemId is required');
      this.uploadFileComponent.triggerUpload();
      return;
    }

    // First, try to get existing documents from the existing documents component
    // (which should already have them loaded)
    let existingDocuments: any[] = [];
    if (this.existingDocumentsComponent) {
      existingDocuments = this.existingDocumentsComponent.getExistingDocuments() || [];
    }

    // If we don't have documents from the display component, fetch them
    if (existingDocuments.length === 0) {
      // Ensure courseItemId is set on the upload component before fetching
      if (this.uploadFileComponent) {
        (this.uploadFileComponent as any).courseItemId = this.courseItemId;
      }

      // Fetch existing documents to check for duplicates
      this.uploadFileComponent.fetchExistingDocuments().subscribe({
        next: (fetchedDocuments) => {
          existingDocuments = fetchedDocuments || [];
          this.performDuplicateCheck(selectedFiles, existingDocuments);
        },
        error: (err) => {
          // If fetch fails, log error but still proceed with upload
          console.error('Failed to fetch existing documents for duplicate check:', err);
          this.uploadFileComponent.triggerUpload();
        },
      });
    } else {
      // Use the already-loaded documents
      this.performDuplicateCheck(selectedFiles, existingDocuments);
    }
  }

  performDuplicateCheck = (selectedFiles: File[], existingDocuments: any[]) => {
    // Always check for duplicates, even if no existing documents
    if (!existingDocuments || !Array.isArray(existingDocuments)) {
      existingDocuments = [];
    }

    // Check for duplicate file names and store document info
    const duplicateFiles: string[] = [];
    selectedFiles.forEach((file: File) => {
      const fileName = file.name.toLowerCase().trim();
      
      // Check each existing document for a match
      const existingDoc = existingDocuments.find((doc: any) => {
        if (!doc || !doc.fileName) return false;
        
        // Decode the stored filename if it's encoded
        let storedFileName = doc.fileName;
        try {
          // Try to decode if it's URL encoded
          storedFileName = decodeURIComponent(storedFileName);
        } catch (e) {
          // If decoding fails, use original
        }
        
        // Compare case-insensitively and trimmed
        return storedFileName.toLowerCase().trim() === fileName;
      });
      
      if (existingDoc) {
        duplicateFiles.push(file.name); // Use original name for display
        this.duplicateDocuments.push({
          assetId: existingDoc.assetId,
          fileName: existingDoc.fileName,
        });
      }
    });

    // Always call handleDuplicateCheck, even if no duplicates found
    this.handleDuplicateCheck(duplicateFiles);
  }

  handleDuplicateCheck = (duplicateFiles: string[]) => {
    // If duplicates found, show confirmation dialog
    if (duplicateFiles.length > 0) {
      const duplicateCount = duplicateFiles.length;
      const fileText = duplicateCount === 1 ? 'file' : 'files';
      const fileNames = duplicateFiles.join(', ');
      const message = duplicateCount === 1
        ? `A document named "${fileNames}" already exists. Would you like to replace it?`
        : `The following ${fileText} already exist: ${fileNames}. Would you like to replace ${duplicateCount === 1 ? 'it' : 'them'}?`;

      const dialogRef = this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Replace Existing Document' + (duplicateCount > 1 ? 's' : ''),
          text: message,
          confirmButtonTitle: 'Yes, replace',
          cancelButtonTitle: 'No, cancel',
          confirmButtonClass: 'green',
        },
        disableClose: true, // Prevent closing without making a choice
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'verified') {
          // User confirmed, delete existing documents first, then upload
          this.deleteDuplicateDocumentsAndUpload();
        }
        // If user cancelled, do nothing - they can cancel the modal or try again
      });
    } else {
      // No duplicates, proceed with upload
      this.uploadFileComponent.triggerUpload();
    }
  }

  deleteDuplicateDocumentsAndUpload = () => {
    if (this.duplicateDocuments.length === 0) {
      // No duplicates to delete, proceed with upload
      this.uploadFileComponent.triggerUpload();
      return;
    }

    // Delete all duplicate documents
    const deleteObservables = this.duplicateDocuments.map((doc) =>
      this.coursesService.removeCourseDocument(this.courseItemId, doc.assetId));

    // Wait for all deletions to complete, then proceed with upload
    forkJoin(deleteObservables).subscribe({
      next: () => {
        // All duplicates deleted, wait a moment to ensure backend has processed deletions
        // then proceed with upload
        setTimeout(() => {
          this.uploadFileComponent.triggerUpload();
        }, 300);
      },
      error: (err) => {
        // If deletion fails, log error but still try to proceed with upload
        // The backend should handle conflicts or the user can manually delete
        console.error('Failed to delete duplicate documents:', err);
        // Wait a moment then proceed with upload anyway
        // This handles cases where deletion partially succeeded or backend is still processing
        setTimeout(() => {
          this.uploadFileComponent.triggerUpload();
        }, 200);
      },
    });
  }

  uploadCompleted = () => {
    // Reload documents after upload
    if (this.uploadFileComponent && this.courseItemId) {
      this.uploadFileComponent.fetchExistingDocuments().subscribe({
        next: () => {
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to reload documents after upload:', err);
        },
      });
    }
    
    // Emit documentsUploaded event before closing modal
    this.documentsUploaded.emit();
    this.closeModalHandler();
  }

  onDocumentDeleted = () => {
    // Reload documents after deletion in both components
    if (this.courseItemId) {
      // Reload in existing documents component
      if (this.existingDocumentsComponent) {
        this.existingDocumentsComponent.fetchExistingDocuments().subscribe({
          next: () => {
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Failed to reload documents after deletion:', err);
          },
        });
      }
      
      // Also reload in upload component to keep them in sync
      if (this.uploadFileComponent) {
        this.uploadFileComponent.fetchExistingDocuments().subscribe({
          next: () => {
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Failed to reload documents in upload component after deletion:', err);
          },
        });
      }
    }
  }
}
